import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template121: Camden Town (Page6 guia-londres). Todo desde backend.
// page.texts[0]=header, texts[1]=intro | sections[0]=Camden Market, sections[1]=listItems, sections[2]=cómo llegar
const Template121 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;
	const introText = page.texts?.[1]?.content;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const camdenImage = getImageSrc("camden");
	const camdenMarketImage = getImageSrc("camden-market");
	const camdenBadge = getCanvasImageUrl("camden-badge");
	const camdenSticker = getCanvasImageUrl("camden-sticker");

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const section0 = sections[0]; // Camden Market
	const listBlock = sections[1]; // listItems (lista de puntos)
	const transportBlock = sections[2]; // Cómo llegar

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const contentColor = textColor || "#444";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					Día 4: {headerText}
					{logoImage && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* PORTADA */}
			<Box sx={{ position: "relative" }}>
				{camdenImage && (
					<img
						src={camdenImage}
						alt=""
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				)}
				{camdenBadge && (
					<img
						src={camdenBadge}
						alt=""
						style={{
							width: 120,
							position: "absolute",
							bottom: -10,
							right: -50,
						}}
					/>
				)}
				{headerText && (
					<Box
						component="h1"
						sx={{
							fontSize: isSmallScreen ? "28px" : "32px",
							position: "absolute",
							bottom: 0,
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							margin: "10px",
						}}
					>
						{headerText}
					</Box>
				)}
			</Box>

			<Box
				sx={{
					padding: "10px 12px",
					flex: 1,
					position: "relative",
				}}
			>
				{introText && (
					<Box
						component="p"
						sx={{
							fontSize: 13,
							color: "#333",
							marginBottom: "12px",
							marginTop: "-8px",
						}}
					>
						{introText}
					</Box>
				)}

				{/* Camden Market (section 0) */}
				{(section0?.title || section0?.body) && (
					<Box data-block-id="text-1" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{camdenMarketImage && (
							<img
								src={camdenMarketImage}
								alt=""
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<Box sx={{ width: camdenMarketImage ? "60%" : "100%" }}>
							{section0?.title && (
								<Box component="h3" sx={h3}>
									{section0.title}
								</Box>
							)}
							{section0?.body && (
								<Box component="p" sx={p}>
									{section0.body}
								</Box>
							)}
						</Box>
						{section0?.sabiasQue?.[0] && (
							<Box sx={{ position: "absolute", top: 30, left: "33%" }}>
								<SabiasQue
									icono={section0.sabiasQue[0].icono}
									texto={section0.sabiasQue[0].texto}
								/>
							</Box>
						)}
						{section0?.sabiasQue?.[1] && (
							<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
								<SabiasQue
									icono={section0.sabiasQue[1].icono}
									texto={section0.sabiasQue[1].texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{/* Lista (section 1 - listItems) */}
				{listBlock?.listItems?.length > 0 && (
					<Box data-block-id="text-2" sx={{ position: "relative" }}>
						<Box
							component="ul"
							sx={{
								fontSize: 13,
								paddingLeft: "20px",
								color: contentColor,
								marginBottom: "12px",
								position: "relative",
							}}
						>
							{listBlock.listItems.map((item, idx) => (
								<Box
									component="li"
									key={idx}
									sx={{
										marginBottom:
											idx < listBlock.listItems.length - 1 ? "8px" : 0,
									}}
								>
									{item.title && <strong>{item.title}</strong>}
									{item.title && item.body ? " " : ""}
									{item.body}
								</Box>
							))}
						</Box>
					</Box>
				)}

				{/* Cómo llegar (section 2) */}
				{(transportBlock?.title || transportBlock?.listItems?.length > 0) && (
					<Box
						data-block-id="text-3"
						sx={{
							backgroundColor: "#f4e4df",
							border: "3px solid #de5624",
							borderRadius: "12px",
							padding: "10px 12px",
							fontSize: 12,
							color: "#333",
							marginTop: isSmallScreen ? "90px" : "12px",
						}}
					>
						{transportBlock?.title && (
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: "8px",
									fontSize: 13,
									fontWeight: 700,
									display: "flex",
									alignItems: "center",
									gap: 6,
									color: "#de5624!important",
									textTransform: "uppercase",
								}}
							>
								{transportBlock.title}
							</Box>
						)}
						{transportBlock?.listItems?.length > 0 && (
							<Box
								component="ul"
								sx={{ paddingLeft: "20px", margin: 0, lineHeight: 1.5 }}
							>
								{transportBlock.listItems.map((item, idx) => (
									<Box component="li" key={idx}>
										{item.title && <strong>{item.title}</strong>}
										{item.title && item.body ? " " : ""}
										{item.body}
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}

				{camdenSticker && (
					<img
						src={camdenSticker}
						alt=""
						style={{
							width: 90,
							position: "absolute",
							bottom: isSmallScreen ? 100 : 5,
							right: 0,
						}}
					/>
				)}
			</Box>
		</Box>
	);
};

Template121.thumbnail = "/images/template-1.png";

export default Template121;
